import { NoSubscriptionOptionInBundle, V2CheckoutFragment } from "@stepapp/core";
import { Order_Discount_Type } from "@stepapp/core/apiTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export enum QuestionId {
  Home = "home",
  Subscription = "subscription",
}

export const useGetServiceSummaryBasicAnswers = ({
  checkout,
}: {
  checkout?: V2CheckoutFragment;
}) => {
  const { t } = useTranslation();
  const commonAnswers = useMemo(() => {
    const answers: {
      id: QuestionId;
      questionTitle: string;
      answerTitle: string;
      answerPrice?: number;
      description: string;
    }[] = [];

    const houseArea = !!checkout?.bundle?.houseArea && {
      id: QuestionId.Home,
      questionTitle: t("screens.homeEdit.surface") as string,
      answerTitle: `${checkout?.address?.floorArea} m2`,
      answerPrice: checkout.orderHouseArea?.price,
      description: "",
    };

    houseArea && answers.push(houseArea);

    if (checkout?.periodicity?.title === NoSubscriptionOptionInBundle) return answers;

    const chosenSubscription = checkout?.periodicity;
    const periodicityDiscount =
      checkout?.orderHouseArea?.discount?.find(
        (item) => item?.type === Order_Discount_Type.Subscription,
      )?.fixedValue || 0;

    const periodicity = !!chosenSubscription && {
      id: QuestionId.Subscription,
      questionTitle: t("screens.serviceSummary.howOften") as string,
      answerTitle: chosenSubscription.title,
      description: `-${chosenSubscription.discount}%`,
      answerPrice: periodicityDiscount ? -periodicityDiscount : 0,
    };

    periodicity && answers.push(periodicity);

    return answers;
  }, [
    checkout?.bundle?.houseArea,
    checkout?.orderHouseArea?.discount,
    checkout?.address?.floorArea,
    checkout?.orderHouseArea?.price,
    checkout?.periodicity,
    t,
  ]);

  const parsedAttributes = useMemo(
    () =>
      checkout?.attributes?.map((item) => ({
        id: item?.attribute.id || "",
        questionTitle: item?.attribute.name || "",
        description: `${item?.price} ${item?.currency}` || "",
        answerTitle: `${item?.quantity && item?.quantity > 1 ? `${item?.quantity}x` : ""} ${item?.values?.map((value) => value?.name).join("\n") || ""}`,
        answerPrice: item?.price,
      })),
    [checkout?.attributes],
  );

  return [...commonAnswers, ...(parsedAttributes || [])];
};
